import type { Ring, Vec2 } from '../../../../geo/types';
import { centroid, ensureCCW } from '../../../../geo/polygon';
import type { PlanFrame } from '../planFrame';
import type { Landmark } from '../../../../data/types';

/**
 * A building's plan measured from its front, the footprint edge that faces the nearest square: u
 * runs along that edge from its left end as seen from the square, v into the footprint.
 */
export interface FrontFrame {
  readonly plan: PlanFrame;
  readonly length: number;
  /** How far the footprint reaches behind the front. */
  readonly depth: number;
}

function unit(v: Vec2): Vec2 {
  const l = Math.hypot(v[0], v[1]);
  return l === 0 ? [0, 0] : [v[0] / l, v[1] / l];
}

/** Index of the counter-clockwise edge whose outside turns most toward `target`. */
function edgeFacing(ring: Ring, target: Vec2): number {
  let best = 0;
  let bestDot = -Infinity;
  ring.forEach((a, i) => {
    const b = ring[(i + 1) % ring.length]!;
    const outward = unit([b[1] - a[1], -(b[0] - a[0])]);
    const toward = unit([target[0] - (a[0] + b[0]) / 2, target[1] - (a[1] + b[1]) / 2]);
    const dot = outward[0] * toward[0] + outward[1] * toward[1];
    if (dot > bestDot) {
      bestDot = dot;
      best = i;
    }
  });
  return best;
}

function nearestSquare(footprint: Ring, squares: readonly Ring[]): Vec2 | null {
  const here = centroid(footprint);
  const centres = squares.map((s) => centroid(s));
  if (centres.length === 0) return null;
  return centres.reduce((a, c) =>
    Math.hypot(c[0] - here[0], c[1] - here[1]) < Math.hypot(a[0] - here[0], a[1] - here[1]) ? c : a
  );
}

/** The frame on the edge facing the nearest square; without squares, on the longest edge. */
export function frontFrameOf(footprint: Ring, squares: readonly Ring[]): FrontFrame {
  const ring = ensureCCW(footprint);
  const square = nearestSquare(ring, squares);
  const lengths = ring.map((a, i) =>
    Math.hypot(ring[(i + 1) % ring.length]![0] - a[0], ring[(i + 1) % ring.length]![1] - a[1])
  );
  const i = square ? edgeFacing(ring, square) : lengths.indexOf(Math.max(...lengths));
  return frameOnEdge(ring, i);
}

function frameOnEdge(ring: Ring, i: number): FrontFrame {
  const a = ring[i]!;
  const b = ring[(i + 1) % ring.length]!;
  const heading = Math.atan2(b[1] - a[1], b[0] - a[0]);
  const c = Math.cos(heading);
  const s = Math.sin(heading);
  const local = ring.map((p): Vec2 => {
    const dx = p[0] - a[0];
    const dy = p[1] - a[1];
    return [dx * c + dy * s, -dx * s + dy * c];
  });
  return {
    plan: { origin: a, heading, ring: local },
    length: Math.hypot(b[0] - a[0], b[1] - a[1]),
    depth: Math.max(...local.map((p) => p[1])),
  };
}

/** The recorded town-hall heading pins its Dam front even when its depth exceeds its width. */
export function stadhuisFrameOf(landmark: Pick<Landmark, 'footprint' | 'heading'>): FrontFrame {
  const ring = ensureCCW(landmark.footprint);
  const heading = landmark.heading + Math.PI;
  const forward: Vec2 = [Math.cos(heading), Math.sin(heading)];
  let edge = 0;
  let best = -Infinity;
  ring.forEach((a, i) => {
    const b = ring[(i + 1) % ring.length]!;
    const d = unit([b[0] - a[0], b[1] - a[1]]);
    const alignment = d[0] * forward[0] + d[1] * forward[1];
    if (alignment > best) {
      best = alignment;
      edge = i;
    }
  });
  return frameOnEdge(ring, edge);
}
